import { API_KEY_PERMISSIONS, formatDateTime, type ApiKeyPermission } from '@erp/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, Plus } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '../../components/ui/alert.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Dialog } from '../../components/ui/dialog.tsx';
import { Field, Input } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, ApiError, errorMessage } from '../../lib/api.ts';
import { useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';

// Mirrors apps/api/src/integrations/api-keys.service.ts.
interface ApiKeyView {
  id: string;
  name: string;
  prefix: string;
  permissions: ApiKeyPermission[];
  createdBy: string;
  createdAt: string;
  lastUsedAt: string | null;
  requests24h: number;
  revokedAt: string | null;
}

const KEYS = ['api-keys'] as const;
const PERMISSIONS = Object.keys(API_KEY_PERMISSIONS) as ApiKeyPermission[];

/**
 * Keys of the public API (HU-WEB-13): for the WordPress plugin and n8n. The full key is
 * shown only once, when created; afterwards only its prefix.
 */
export function ApiKeysPage() {
  const keys = useQuery({ queryKey: KEYS, queryFn: () => api<ApiKeyView[]>('/api-keys') });
  const { data: session } = useSession();
  const tz = session?.tenant.timezone;
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<ApiKeyView | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Claves de API</h1>
          <p className="text-sm text-muted-foreground">
            Para conectar el sitio web (plugin de WordPress) y herramientas como n8n. Cada clave
            solo puede lo que marques.
          </p>
        </div>
        <Button
          variant="brand"
          onClick={() => {
            setCreating(true);
          }}
        >
          <Plus /> Nueva clave
        </Button>
      </div>
      {keys.isPending ? (
        <Loading />
      ) : keys.error ? (
        <Alert tone="danger">{errorMessage(keys.error)}</Alert>
      ) : keys.data.length === 0 ? (
        <Card className="text-center text-sm text-muted-foreground">Todavía no hay claves.</Card>
      ) : (
        <Card className="p-0">
          <ul className="divide-y divide-border">
            {keys.data.map((k) => (
              <li key={k.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="min-w-0 space-y-1 text-sm">
                  <p className="flex flex-wrap items-center gap-2">
                    <KeyRound className="size-4 text-muted-foreground" aria-hidden="true" />
                    <span className="font-medium">{k.name}</span>
                    <span className="font-mono text-muted-foreground">{k.prefix}…</span>
                    {k.revokedAt && <Badge tone="danger">Revocada</Badge>}
                  </p>
                  <p className="text-muted-foreground">
                    {k.permissions.map((p) => API_KEY_PERMISSIONS[p].label).join(' · ')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Creada el {formatDateTime(k.createdAt, tz)} por {k.createdBy} ·{' '}
                    {k.lastUsedAt
                      ? `último uso el ${formatDateTime(k.lastUsedAt, tz)}`
                      : 'sin usar todavía'}{' '}
                    · {k.requests24h} peticiones en 24 h
                    {k.revokedAt ? ` · revocada el ${formatDateTime(k.revokedAt, tz)}` : ''}
                  </p>
                </div>
                {!k.revokedAt && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setRevoking(k);
                    }}
                  >
                    Revocar
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
      {creating && (
        <CreateDialog
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
      {revoking && (
        <RevokeDialog
          apiKey={revoking}
          onClose={() => {
            setRevoking(null);
          }}
        />
      )}
    </div>
  );
}

function CreateDialog({ onClose }: { onClose: () => void }) {
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState<ApiKeyPermission[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await api<{ key: string }>('/api-keys', {
        method: 'POST',
        body: { name, permissions },
      });
      setCreated(result.key);
      await client.invalidateQueries({ queryKey: KEYS });
    } catch (e) {
      setError(
        e instanceof ApiError && e.issues.length > 0
          ? (e.issues[0]?.message ?? errorMessage(e))
          : errorMessage(e),
      );
    } finally {
      setSaving(false);
    }
  };

  if (created) {
    return (
      <Dialog open onClose={onClose} title="Clave creada">
        <div className="space-y-4">
          <Alert tone="warning" title="Cópiala ahora">
            Es la única vez que se muestra completa. Si la pierdes, revócala y crea otra.
          </Alert>
          <Field label="Clave de API">
            {(a11y) => (
              <Input
                {...a11y}
                readOnly
                value={created}
                className="font-mono"
                onFocus={(e) => {
                  e.currentTarget.select();
                }}
              />
            )}
          </Field>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={onClose}>
              Listo
            </Button>
            <Button
              variant="brand"
              onClick={() =>
                void navigator.clipboard.writeText(created).then(() => {
                  setCopied(true);
                })
              }
            >
              <Copy /> {copied ? 'Copiada' : 'Copiar'}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            En WordPress va en <span className="font-mono">wp-config.php</span>, nunca en el tema ni
            en un correo.
          </p>
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog open onClose={onClose} title="Nueva clave de API">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void create();
        }}
      >
        <Field label="Nombre" hint="Para reconocerla: «Sitio web», «n8n (Meta)»…">
          {(a11y) => (
            <Input
              {...a11y}
              maxLength={80}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          )}
        </Field>
        <fieldset className="space-y-1">
          <legend className="mb-1 text-sm font-medium">Qué puede hacer</legend>
          {PERMISSIONS.map((p) => (
            <label key={p} className="flex min-h-11 items-start gap-3 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 size-5"
                checked={permissions.includes(p)}
                onChange={(e) => {
                  setPermissions(
                    e.target.checked ? [...permissions, p] : permissions.filter((x) => x !== p),
                  );
                }}
              />
              <span>
                <span className="font-medium">{API_KEY_PERMISSIONS[p].label}</span>
                <span className="block text-muted-foreground">{API_KEY_PERMISSIONS[p].help}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="brand"
            loading={saving}
            disabled={!name.trim() || permissions.length === 0}
          >
            Crear clave
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function RevokeDialog({ apiKey, onClose }: { apiKey: ApiKeyView; onClose: () => void }) {
  const client = useQueryClient();
  const notify = useToast();
  const [saving, setSaving] = useState(false);
  const revoke = async () => {
    setSaving(true);
    try {
      await api(`/api-keys/${apiKey.id}/revoke`, { method: 'POST' });
      await client.invalidateQueries({ queryKey: KEYS });
      notify('Clave revocada: ya no funciona.', 'success');
      onClose();
    } catch (e) {
      notify(errorMessage(e), 'error');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Revocar «${apiKey.name}»`}
      description="Deja de funcionar al instante: lo que la use (el sitio web, n8n) dejará de conectarse hasta que le pongas una clave nueva. No se puede deshacer."
    >
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="danger" loading={saving} onClick={() => void revoke()}>
          Revocar clave
        </Button>
      </div>
    </Dialog>
  );
}
